import { DiscordIcon, InstagramIcon, XIcon } from "@/components/socialIcons";
import {
  GAME_NAME,
  OTHER_EDITION_NAME,
  OTHER_EDITION_URL,
} from "@/config/game";
import { DISCORD_URL, INSTAGRAM_URL, X_URL } from "@/config/social";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { isMobile } from "react-device-detect";
import { useSelector } from "react-redux";
import { NavLink } from "react-router";

import { globalStateSelector } from "../../../redux/globalState/globalStateSlice";

type FooterLink = { label: string; to: string; external?: boolean };

// The other edition is its own deploy, not a route, so it opens in a new tab.
const editionLink: FooterLink = {
  label: OTHER_EDITION_NAME,
  to: OTHER_EDITION_URL,
  external: true,
};

const linkClass =
  "text-sm text-dark-tertiary hover:text-white transition-colors w-fit";

// Both rows have to line up, so they share one gutter.
const rowClass = "mx-auto w-full max-w-content px-4 large-desktop:px-8";

// Padding, not margin, on the ad gutter so the background covers it.
const sectionClass =
  "z-10 bg-primary-dark border-t border-neutral-800 ad-gutter-pad";

const FooterAnchor = ({
  link: { label, to, external },
  className,
}: {
  link: FooterLink;
  className: string;
}) =>
  external ? (
    <a
      href={to}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {label}
    </a>
  ) : (
    <NavLink to={to} className={className}>
      {label}
    </NavLink>
  );

const LinkColumn = ({
  title,
  links,
}: {
  title: string;
  links: FooterLink[];
}) => (
  <nav className="flex flex-col gap-y-3">
    <h3 className="text-sm font-semibold text-dark-secondary">{title}</h3>
    {links.map((link) => (
      <FooterAnchor key={link.to} link={link} className={linkClass} />
    ))}
  </nav>
);

// Touch swaps the grid for one wrapped run of links. Brighter than the desktop
// links: with no heading above them the labels carry the block on their own.
// The divider rides on the link so a wrap can never strand it on its own line.
const InlineLinks = ({ links }: { links: FooterLink[] }) => (
  <nav className="flex flex-row flex-wrap items-center gap-x-3 gap-y-1">
    {links.map((link) => (
      <FooterAnchor
        key={link.to}
        link={link}
        className="py-1 pr-3 border-r border-panel-foreground last:border-r-0 last:pr-0 text-sm font-medium text-dark-secondary hover:text-white transition-colors"
      />
    ))}
  </nav>
);

const Social = ({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    title={label}
    className="flex size-9 items-center justify-center rounded-full bg-panel-background text-dark-tertiary hover:bg-panel-foreground hover:text-white transition-colors"
  >
    {children}
  </a>
);

const Footer = () => {
  const { t } = useTranslation(["nav", "common"]);
  const globalState = useSelector(globalStateSelector);

  if (!globalState.showFooter) return null;

  const exploreLinks: FooterLink[] = [
    { label: t("nav:links.sbcs"), to: "/sbc" },
    { label: t("nav:links.squadBuilder"), to: "/squad-builder" },
    { label: t("nav:links.players"), to: "/players" },
    { label: t("nav:links.evolutions"), to: "/evolutions/builder" },
    { label: t("nav:links.metaRating"), to: "/meta-rating" },
    editionLink,
  ];
  const companyLinks: FooterLink[] = [
    { label: t("nav:links.whoAreWeQuestion"), to: "/who-are-we" },
    { label: t("nav:links.contact"), to: "/contact" },
    { label: t("nav:links.becomePartner"), to: "/become-a-partner" },
    { label: t("nav:links.subscription"), to: "/subscription" },
  ];
  const legalLinks: FooterLink[] = [
    { label: t("nav:footer.faq"), to: "/legal/faq" },
    { label: t("common:privacyPolicy"), to: "/legal/privacy" },
    { label: t("common:termsOfService"), to: "/legal/tos" },
  ];

  const copyright = (
    <p className="text-xs text-dark-tertiary">
      {t("nav:footer.copyright", { game: GAME_NAME })}
    </p>
  );

  // On touch: no logo, no tagline, no headings, and none of the product links —
  // the nav's drawer already carries those. Edition first, as on desktop; at
  // the end it wraps onto an extra line more often.
  if (isMobile) {
    return (
      <section className={sectionClass}>
        <div className={cn(rowClass, "py-5 flex flex-col gap-y-3")}>
          <InlineLinks links={[editionLink, ...companyLinks, ...legalLinks]} />
          {copyright}
        </div>
      </section>
    );
  }

  return (
    <section className={sectionClass}>
      {/* One grid rather than brand-plus-a-link-group: at max-w-content a
          justify-between row leaves a void in the middle. */}
      <div
        className={cn(
          rowClass,
          "py-10 grid grid-cols-2 gap-x-8 gap-y-10 desktop:grid-cols-4",
        )}
      >
        <div className="flex flex-col gap-y-4 max-w-xs col-span-2 desktop:col-span-1">
          <img
            src={import.meta.env.VITE_APP_ASSETS_BASE_URL + "/easysbc1.png"}
            alt={t("common:brand")}
            className="h-7 w-fit"
          />
          <p className="text-sm text-dark-tertiary">
            {t("nav:footer.tagline", { game: GAME_NAME })}
          </p>
          <div className="flex flex-row gap-x-2 mt-1">
            <Social href={DISCORD_URL} label={t("common:discord")}>
              <DiscordIcon className="h-3.5 w-auto" />
            </Social>
            <Social href={X_URL} label={t("nav:links.x")}>
              <XIcon className="h-3.5 w-auto" />
            </Social>
            <Social href={INSTAGRAM_URL} label={t("nav:links.instagram")}>
              <InstagramIcon className="h-4 w-auto" />
            </Social>
          </div>
        </div>

        <LinkColumn title={t("nav:footer.explore")} links={exploreLinks} />
        <LinkColumn title={t("nav:footer.company")} links={companyLinks} />
        <LinkColumn title={t("nav:footer.legalHeading")} links={legalLinks} />
      </div>

      <div className="border-t border-neutral-800">
        <div
          className={cn(
            rowClass,
            "py-5 flex flex-row items-center justify-between gap-x-4",
          )}
        >
          {copyright}
          {import.meta.env.VITE_APP_SHOW_COMPONENTS && (
            <NavLink to={"/components"} className="shrink-0">
              <img
                className="w-6"
                src="https://ionicframework.com/docs/icons/logo-react-icon.png"
                // eslint-disable-next-line i18next/no-literal-string -- asset id, not copy
                alt="react-icon"
              />
            </NavLink>
          )}
        </div>
      </div>
    </section>
  );
};

export default Footer;
